// src/utils/pdfExport1.js
import html2pdf from 'html2pdf.js';
import { fetchSocieteInfos, getSocieteInfos } from "./societe";
import { formatCurrencyForExport, formatNumberForExport } from "./exportFormatters";
import logo1 from "../assets/img/IMG-20250130-WA0008.jpg";
import { COLORS } from '../constants/colors';

/**
 * Génère un PDF élégant pour le rapport CA avec la charte graphique
 */
export const generateCAPDF = async (data, selectedYear) => {
  return new Promise(async (resolve, reject) => {
    try {
      // 🔥 Récupérer les informations société depuis l'API (asynchrone)
      console.log("📡 Récupération des infos société via API...");
      let societe = await fetchSocieteInfos();
      console.log("✅ Infos société récupérées:", societe);
      
      // Fallback si l'API ne retourne rien
      if (!societe || Object.keys(societe).length === 0) {
        console.warn("⚠️ API retourné vide, utilisation localStorage");
        societe = getSocieteInfos();
      }
      
      const mois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      
      // ===== COULEURS DE LA CHARTE =====
      const primaryColor = COLORS.primary;      // #055697 (bleu)
      const secondaryColor = COLORS.secondary;  // #C42625 (rouge)
      const primaryLight = primaryColor + '15'; // version très claire pour fond
      const secondaryLight = secondaryColor + '15';
      
      // Convertir le logo en base64 pour l'utiliser dans le HTML
      const getLogoBase64 = () => {
        return new Promise((resolveLogo) => {
          fetch(logo1)
            .then(response => response.blob())
            .then(blob => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolveLogo(reader.result);
              };
              reader.readAsDataURL(blob);
            })
            .catch(() => {
              resolveLogo('');
            });
        });
      };
      
      // Générer le HTML du graphique d'évolution mensuelle
      const generateEvolutionHTML = () => {
        if (!data.ca_mensuel || data.ca_mensuel.length === 0) return '<p>Aucune donnée disponible</p>';
        
        let html = '<table style="width: 100%; border-collapse: collapse;">';
        // En-tête avec fond primary
        html += `<thead><tr><th style="background: ${primaryColor}; color: white; padding: 10px; text-align: left;">Mois</th><th style="background: ${primaryColor}; color: white; padding: 10px; text-align: right;">Montant (FCFA)</th><th style="background: ${primaryColor}; color: white; padding: 10px; text-align: center;">Variation</th></tr></thead>`;
        html += '<tbody>';
        
        data.ca_mensuel.forEach((montant, index) => {
          if (montant > 0) {
            const previous = index > 0 ? data.ca_mensuel[index - 1] : montant;
            const variation = montant - previous;
            const variationPercent = previous > 0 ? (variation / previous * 100).toFixed(1) : 0;
            const isPositive = variation >= 0;
            const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
            
            html += `<tr style="background: ${bgColor};">`;
            html += `<td style="padding: 10px; font-weight: 600;">${mois[index]}</td>`;
            html += `<td style="padding: 10px; text-align: right;">${formatCurrencyForExport(montant)}</td>`;
            html += `<td style="padding: 10px; text-align: center;">`;
            if (index > 0) {
              html += `<span style="background: ${isPositive ? '#d4edda' : '#f8d7da'}; color: ${isPositive ? '#28a745' : '#dc3545'}; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: 600;">${isPositive ? '▲' : '▼'} ${Math.abs(variationPercent)}%</span>`;
            } else {
              html += '<span style="color: #6c757d;">—</span>';
            }
            html += `</span></td>`;
          }
        });
        
        html += '</tbody></table>';
        return html;
      };
      
      // Générer le HTML des top produits
      const generateTopProductsHTML = () => {
        if (!data.top_produits || data.top_produits.length === 0) {
          return '<tr><td colspan="4" style="padding: 20px; text-align: center;">Aucune donnée disponible</td></tr>';
        }
        
        let html = '';
        data.top_produits.slice(0, 5).forEach((produit, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px;">${produit.designation || '-'}</td>`;
          html += `<td style="padding: 10px; color: #6c757d;">${produit.reference || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center;">${formatNumberForExport(produit.quantite_vendue || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; font-weight: 600; color: ${secondaryColor};">${formatCurrencyForExport(produit.montant_total || 0)}</td>`;
          html += `</tr>`;
        });
        return html;
      };
      
      // Générer le HTML des top clients
      const generateTopClientsHTML = () => {
        if (!data.top_clients || data.top_clients.length === 0) {
          return '<tr><td colspan="4" style="padding: 20px; text-align: center;">Aucune donnée disponible</td></tr>';
        }
        
        let html = '';
        data.top_clients.slice(0, 5).forEach((client, index) => {
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px; font-weight: 600;">${client.raison_sociale || '-'}</td>`;
          html += `<td style="padding: 10px; color: #6c757d;">${client.code_client || '-'}</td>`;
          html += `<td style="padding: 10px; text-align: center;">${formatNumberForExport(client.nb_factures || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: right; font-weight: 600; color: ${secondaryColor};">${formatCurrencyForExport(client.total_achats || 0)}</td>`;
          html += `</tr>`;
        });
        return html;
      };
      
      // Générer le HTML des catégories
      const generateCategoriesHTML = () => {
        if (!data.ventes_categories || data.ventes_categories.length === 0) {
          return '<tr><td colspan="3" style="padding: 20px; text-align: center;">Aucune donnée disponible</td></tr>';
        }
        
        let html = '';
        data.ventes_categories.forEach((cat, index) => {
          const percentage = ((parseFloat(cat.montant_total) / (data.totaux?.ca_annuel || 1)) * 100).toFixed(1);
          const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
          const badgeBg = percentage > 20 ? '#d4edda' : '#fff3cd';
          const badgeColor = percentage > 20 ? '#155724' : '#856404';
          
          html += `<tr style="background: ${bgColor};">`;
          html += `<td style="padding: 10px;">${cat.nom_categorie || 'Sans catégorie'}</td>`;
          html += `<td style="padding: 10px; text-align: right; color: ${primaryColor};">${formatCurrencyForExport(cat.montant_total || 0)}</td>`;
          html += `<td style="padding: 10px; text-align: center;"><span style="background: ${badgeBg}; color: ${badgeColor}; padding: 3px 10px; border-radius: 4px; font-size: 9px; font-weight: 600;">${percentage}%</span></td>`;
          html += `</tr>`;
        });
        return html;
      };
      
      // Générer le HTML avec le logo
      const generateHTMLWithLogo = (logoBase64) => {
        return `<!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Rapport Financier ${selectedYear}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
            body { background: #fff; padding: 25px; font-size: 11px; color: #222; }
            .report-container { max-width: 1200px; margin: 0 auto; background: white; }
            
            .header { margin-bottom: 20px; }
            .header-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
            .logo-section { flex: 0 0 auto; }
            .logo-img { height: 50px; width: auto; object-fit: contain; border-radius: 0; }
            .company-section { text-align: right; }
            .company-name { font-size: 16px; font-weight: 700; color: ${primaryColor}; margin-bottom: 5px; }
            .company-address { font-size: 9px; color: #6c757d; margin-bottom: 3px; }
            .company-contact { font-size: 9px; color: #6c757d; }
            
            .title-section { text-align: center; margin: 10px 0 25px 0; padding-bottom: 15px; border-bottom: 2px solid ${secondaryColor}; }
            .title-section h1 { font-size: 18px; font-weight: 600; color: ${primaryColor}; margin-bottom: 5px; }
            .title-section p { font-size: 10px; color: #6c757d; }
            .date { font-size: 8px; color: #adb5bd; margin-top: 5px; }
            
            .info-bar { display: flex; justify-content: space-between; margin-bottom: 25px; padding: 15px; background: #f8f9fa; border-radius: 6px; border-left: 4px solid ${secondaryColor}; }
            .info-item { text-align: center; flex: 1; border-right: 1px solid #e0e0e0; }
            .info-item:last-child { border-right: none; }
            .info-label { font-size: 8px; text-transform: uppercase; color: #6c757d; letter-spacing: 0.5px; margin-bottom: 5px; }
            .info-value { font-size: 14px; font-weight: 700; color: ${primaryColor}; }
            
            .section { margin-bottom: 25px; }
            .section-title { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; border-left: 3px solid ${secondaryColor}; padding-left: 10px; margin-bottom: 12px; color: ${primaryColor}; }
            
            .stats-grid { display: flex; gap: 12px; margin-bottom: 25px; flex-wrap: wrap; }
            .stat-card { flex: 1; min-width: 120px; padding: 12px; background: #f8f9fa; border-radius: 6px; border-left: 3px solid ${secondaryColor}; }
            .stat-card h3 { font-size: 9px; color: #6c757d; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
            .stat-card .number { font-size: 14px; font-weight: 700; color: ${primaryColor}; }
            .stat-card .small { font-size: 8px; color: #adb5bd; margin-top: 4px; }
            
            table { width: 100%; border-collapse: collapse; }
            th { background: ${primaryColor}; color: white; padding: 10px 12px; text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid ${secondaryColor}; }
            td { font-size: 10px; padding: 8px 12px; border-bottom: 1px solid #e0e0e0; }
            tr:nth-child(even) { background: #fafbfc; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            
            .summary { margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 6px; border-left: 4px solid ${secondaryColor}; }
            .summary h4 { font-size: 11px; margin-bottom: 8px; color: ${secondaryColor}; }
            .summary p { font-size: 9px; margin-bottom: 5px; line-height: 1.4; color: #2c3e50; }
            .summary strong { color: ${primaryColor}; }
            
            .footer { margin-top: 25px; padding-top: 15px; border-top: 1px solid ${secondaryColor}; text-align: center; font-size: 8px; color: #adb5bd; }
            
            @media print { 
              body { padding: 0; } 
              th { background: ${primaryColor} !important; border-bottom: 2px solid ${secondaryColor} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .info-bar { background: #f8f9fa !important; }
              .stat-card { background: #f8f9fa !important; }
              .summary { background: #f8f9fa !important; }
              .company-name { color: ${primaryColor} !important; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="header-top">
                <div class="logo-section">
                  ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="logo-img" />` : '<div style="width: 70px;"></div>'}
                </div>
                <div class="company-section">
                  <div class="company-name">${societe.nom}</div>
                  <div class="company-address">${societe.adresse}</div>
                  <div class="company-contact">Tél: ${societe.telephone} | Email: ${societe.email}</div>
                  <div class="company-contact">RC: ${societe.rc} | NIF: ${societe.nif}</div>
                </div>
              </div>
            </div>
            
            <div class="title-section">
              <h1>RAPPORT FINANCIER ${selectedYear}</h1>
              <p>Analyse des performances commerciales</p>
              <div class="date">Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</div>
            </div>
            
            <div class="info-bar">
              <div class="info-item"><div class="info-label">CA ANNUEL</div><div class="info-value">${formatCurrencyForExport(data.totaux?.ca_annuel || 0)}</div></div>
              <div class="info-item"><div class="info-label">CA MENSUEL</div><div class="info-value">${formatCurrencyForExport(data.totaux?.ca_mensuel || 0)}</div></div>
              <div class="info-item"><div class="info-label">CLIENTS ACTIFS</div><div class="info-value">${formatNumberForExport(data.totaux?.nb_clients || 0)}</div></div>
              <div class="info-item"><div class="info-label">CRÉANCES</div><div class="info-value">${formatCurrencyForExport(data.totaux?.creances_total || 0)}</div></div>
            </div>
            
            <div class="section">
              <div class="section-title">STATISTIQUES GÉNÉRALES</div>
              <div class="stats-grid">
                <div class="stat-card"><h3>CA Annuel</h3><div class="number">${formatCurrencyForExport(data.totaux?.ca_annuel || 0)}</div><div class="small">FCFA</div></div>
                <div class="stat-card"><h3>CA Mensuel moyen</h3><div class="number">${formatCurrencyForExport((data.totaux?.ca_annuel || 0) / 12)}</div><div class="small">FCFA/mois</div></div>
                <div class="stat-card"><h3>Panier moyen</h3><div class="number">${formatCurrencyForExport((data.totaux?.ca_annuel || 0) / (data.totaux?.nb_clients || 1))}</div><div class="small">par client</div></div>
                <div class="stat-card"><h3>Taux créances</h3><div class="number">${(((data.totaux?.creances_total || 0) / (data.totaux?.ca_annuel || 1)) * 100).toFixed(1)}%</div><div class="small">du CA</div></div>
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">ÉVOLUTION MENSUELLE DU CA</div>
              <div style="margin: 15px 0;">
                ${generateEvolutionHTML()}
              </div>
            </div><br>
            
            <div class="section">
              <div class="section-title">TOP 5 PRODUITS</div>
              <div style="overflow-x: auto;">
                <table>
                  <thead><tr><th>Désignation</th><th>Référence</th><th class="text-center">Quantité</th><th class="text-right">Montant (FCFA)</th></tr></thead>
                  <tbody>${generateTopProductsHTML()}</tbody>
                </table>
              </div>
            </div><br>
            
            <div class="section">
              <div class="section-title">TOP 5 CLIENTS</div>
              <div style="overflow-x: auto;">
                <table>
                  <thead><tr><th>Client</th><th>Code</th><th class="text-center">Factures</th><th class="text-right">Total achats (FCFA)</th></tr></thead>
                  <tbody>${generateTopClientsHTML()}</tbody>
                </table>
              </div>
            </div><br>
            
            <div class="section">
              <div class="section-title">VENTES PAR CATÉGORIE</div>
              <div style="overflow-x: auto;">
                <table>
                  <thead><tr><th>Catégorie</th><th class="text-right">Montant (FCFA)</th><th class="text-center">Part du CA</th></tr></thead>
                  <tbody>${generateCategoriesHTML()}</tbody>
                </table>
              </div>
            </div>
            
            <div class="summary">
              <h4>RÉSUMÉ EXÉCUTIF</h4>
              <p>Chiffre d'affaires annuel : <strong>${formatCurrencyForExport(data.totaux?.ca_annuel || 0)} FCFA</strong></p>
              <p>Moyenne mensuelle : <strong>${formatCurrencyForExport((data.totaux?.ca_annuel || 0) / 12)} FCFA</strong></p>
              <p>Nombre de clients actifs : <strong>${formatNumberForExport(data.totaux?.nb_clients || 0)}</strong></p>
              <p>Créances totales : <strong>${formatCurrencyForExport(data.totaux?.creances_total || 0)} FCFA</strong> (${(((data.totaux?.creances_total || 0) / (data.totaux?.ca_annuel || 1)) * 100).toFixed(1)}% du CA)</p>
              ${data.top_produits && data.top_produits[0] ? `<p>Produit le plus vendu : <strong>${data.top_produits[0].designation}</strong> (${formatNumberForExport(data.top_produits[0].quantite_vendue)} unités)</p>` : ''}
              ${data.top_clients && data.top_clients[0] ? `<p>Meilleur client : <strong>${data.top_clients[0].raison_sociale}</strong> (${formatCurrencyForExport(data.top_clients[0].total_achats)} FCFA)</p>` : ''}
            </div>
            
            <div class="footer">
              <div>${societe.nom} • Système de gestion commerciale</div>
              <div style="margin-top: 5px;">Document généré automatiquement</div>
            </div>
          </div>
        </body>
        </html>`;
      };
      
      // Charger le logo et générer le PDF
      const logoBase64 = await getLogoBase64();
      const htmlContent = generateHTMLWithLogo(logoBase64);
      
      const options = {
        margin: [8, 8, 8, 8],
        filename: `Rapport_CA_${selectedYear}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      document.body.appendChild(element);
      
      html2pdf().set(options).from(element).save().then(() => {
        document.body.removeChild(element);
        resolve(true);
      }).catch((error) => {
        document.body.removeChild(element);
        reject(error);
      });
      
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      reject(error);
    }
  });
};